import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../store/useAuth'
import { useEditor } from '../store/useEditor'
import { listProjects, type ProjectSummary } from '../lib/db'
import type { LightsOsDocument } from '../types/document'
import { BRAND } from '../lib/brand'
import { useIsPhone } from '../lib/useIsPhone'
import JobOverview from './JobOverview'
import Today from './Today'
import Schedule from './Schedule'
import Estimates from './Estimates'
import Invoices from './Invoices'
import Settings from './Settings'
import Team from './Team'
import Stock from './Stock'
import JobsBoard from './JobsBoard'
import Costs from './Costs'
import DesignQuote from './DesignQuote'
import Projects from './Projects'
import Customers from './Customers'
import Pipeline from './Pipeline'
import Business from './Business'
import Billing from './Billing'
import TrialNotice from './TrialNotice'
import { Sheet } from './bits'

import {
  IconBilling,
  IconBusiness,
  IconDesign,
  IconJobs,
  IconPeople,
  IconSpark,
  IconBoard,
  IconRows,
  IconCalendar,
  IconDoc,
  IconMoney,
  IconMoreDots,
  IconPhoto,
  IconTool,
  IconBox,
} from './icons'

/**
 * WHAT A CONTRACTOR OPENS THE APP TO, ONCE THERE IS A SEASON IN HERE.
 *
 * Four destinations and a door to the photograph. The editor used to be the
 * whole product and the job list a sheet over it; by October the truth is the
 * other way round. A man opens this in a driveway to see who has not answered,
 * and starts a new house from here rather than arriving in one.
 *
 * ONE ROOM, TWO LIGHTS. Everything here is the light workspace; the editor
 * behind it stays dark and photo first. The whole shell carries the ws class
 * and workspace.css is loaded last, which is how it wins without app.css
 * being edited or the editor's own rules being touched.
 *
 * THE NAV IS REPLACED ON A PHONE, NOT SQUEEZED. A 186px sidebar folded onto a
 * 390px screen is a sidebar nobody can read; the same four destinations become
 * a bar under the thumb. See the media query in styles/workspace.css.
 *
 * IT OWNS NO JOB DATA. The list is still Projects, the grouping is still
 * Customers, the letterhead is still the store's Company. This is a frame.
 */

type Where =
  | 'today'
  | 'schedule'
  | 'customers'
  | 'jobs'
  | 'estimates'
  | 'invoices'
  | 'stock'
  | 'costs'
  | 'settings'

/*
 * TODAY IS FIRST, AND IT IS WHERE SIGNING IN LANDS.
 *
 * The list of houses is still one press away and is still the thing most
 * sessions end up in. It is just no longer the answer to a question nobody
 * asked. See ui/Today.tsx.
 */
/*
 * THE V0.2 DESTINATIONS, IN THE PROTOTYPE'S ORDER.
 *
 * Design & Quote is new and is the middle of the product: one job, five steps,
 * measuring through to the customer's yes. Money & Materials gathers what used
 * to be Business and Billing plus the parts of a job that are stock rather
 * than paperwork, which is how a contractor actually splits them.
 */
/*
 * THE DESTINATIONS ARE GROUPED, as the design draws them. Five in one
 * undifferentiated run is a list; three groups is a shape somebody learns
 * once and then navigates by position rather than by reading.
 */
/*
 * A LABEL OVER ONE ITEM IS NOT A GROUP. Overview sat above Today alone and
 * Others sat above Money & Materials alone, so two of the three headings were
 * decoration: they cost a line of the sidebar each and told nobody anything.
 *
 * Overview & planning has two members now, which is what it was always meant
 * to have: Today is the question and the week is where it gets answered.
 * Others is still one, and stays one until inventory lands beside the money.
 */
/*
 * NINE, AND EVERY ONE OF THEM IS A THING A CONTRACTOR ASKS FOR BY NAME.
 *
 * There were twelve, and the last group was a drawer of five called Others,
 * which is what a nav is called when nobody decided. The test a destination
 * has to pass is whether somebody would go looking for it under that word.
 * In October he thinks who has not answered my quotes, so Estimates stays. In
 * January he thinks who owes me, so Invoices stays. When a box arrives he
 * thinks stock. Nobody has ever thought I will look under Others.
 *
 * Three failed that test and are gone as destinations:
 *
 * DESIGN & QUOTE was not a place, it is what you do to a job, and it carried
 * a second copy of the list of houses to prove it. It is a strip of tabs on
 * the job now.
 *
 * BUSINESS and TEAM are both setup. A letterhead, a subscription and a roster
 * are touched twice a year, and splitting "set up my shop" across three
 * destinations with no rule a person could guess is worse than one screen
 * with tabs on it.
 */
const GROUPS: { label: string; ids: Where[] }[] = [
  { label: 'Overview & planning', ids: ['today', 'schedule'] },
  { label: 'The work', ids: ['customers', 'jobs', 'estimates', 'invoices'] },
  { label: 'Money & materials', ids: ['stock', 'costs'] },
  /* No heading. A label over a run of one is a heading with nothing to head. */
  { label: '', ids: ['settings'] },
]


const PLACES: { id: Where; label: string; short: string; Icon: typeof IconJobs }[] = [
  { id: 'today', label: 'Today', short: 'Today', Icon: IconSpark },
  /*
   * THE WEEK SITS UNDER TODAY, because it answers the question Today raises.
   * Today says what is late and what is waiting; the schedule is where a
   * person does something about it. Grouping it here also gives Overview &
   * planning a second member, which is what a heading over a run of one was
   * always missing.
   */
  { id: 'schedule', label: 'Schedule', short: 'Schedule', Icon: IconCalendar },
  { id: 'customers', label: 'Customers', short: 'Customers', Icon: IconPeople },
  /*
   * ESTIMATES IS NOT THE JOB LIST WITH A FILTER ON IT. The job list is sorted
   * by what you touched last; this is sorted by what you have not touched,
   * which is the opposite question and the one a season turns on.
   */
  { id: 'estimates', label: 'Estimates', short: 'Estimates', Icon: IconDoc },
  /*
   * INVOICES IS NOT MONEY & MATERIALS. That drawer is the contractor's own
   * letterhead and his subscription: what he pays for. This is what he is
   * owed, which is the other direction entirely and the one a season is
   * measured by.
   */
  { id: 'invoices', label: 'Invoices', short: 'Invoices', Icon: IconMoney },
  { id: 'jobs', label: 'Jobs', short: 'Jobs', Icon: IconJobs },
  /*
   * STOCK IS ITS OWN DESTINATION RATHER THAN A THIRD BLOCK UNDER MONEY &
   * MATERIALS, which is where the nav comment always said it would land. A
   * letterhead and a subscription are settings somebody touches twice a year;
   * a catalog with a movement on it is a working screen somebody opens every
   * time a box arrives. Stacking them made one destination three thousand
   * pixels long.
   */
  { id: 'stock', label: 'Stock', short: 'Stock', Icon: IconBox },
  /*
   * COSTS IS THE OTHER DIRECTION FROM INVOICES. Invoices is what is owed to
   * this business; this is what it paid out, and the two are never mixed on
   * one screen because cash collected and money spent answer different
   * questions and the spec says so out loud.
   */
  { id: 'costs', label: 'Costs', short: 'Costs', Icon: IconMoney },
  /*
   * SETTINGS IS SETTING UP THE SHOP, all of it. How a new quote opens, the
   * letterhead a customer reads, the roster a visit can name, and the pass.
   * These were three destinations, and nothing about them told a person which
   * of the three held the thing they wanted.
   *
   * TEAM IS STILL NOT PERMISSIONS. Nobody on it can sign in, because there is
   * nothing to sign in to. It is who goes out, so a visit can name them and
   * the schedule can stop assuming one crew.
   */
  { id: 'settings', label: 'Settings', short: 'Settings', Icon: IconTool },
]

/*
 * FIVE ON THE PHONE BAR, AND THE FIFTH IS A DOOR.
 *
 * 390px over six destinations is 65px each, and Customers alone is wider than
 * that at a readable size, so five is the ceiling and it is a hard one. There
 * are seven destinations now and the build order has another fifteen behind
 * them, so a bar that holds every destination was never going to survive. Four
 * places and a way to the rest is a shape that does.
 *
 * WHICH FOUR. What somebody opens in a truck: what is late, what is booked,
 * whose house, which job. Design & Quote comes off despite being the middle of
 * the product, because arriving there with nothing selected shows the job list
 * anyway, which is the tab beside it, and New design already sits in the phone
 * header. Money & Materials comes off because it is the letterhead and the
 * subscription, which is desk work.
 */
type SetupTab = 'quotes' | 'letterhead' | 'team' | 'plan'

const SETUP_TABS: { id: SetupTab; label: string }[] = [
  { id: 'quotes', label: 'Quote defaults' },
  { id: 'letterhead', label: 'Letterhead' },
  { id: 'team', label: 'Team' },
  { id: 'plan', label: 'Plan' },
]

const PHONE_IDS: Where[] = ['today', 'schedule', 'jobs', 'customers']

export default function Workspace({
  onStartNew,
  onOpenQuote,
}: {
  onStartNew: () => void
  /** Opens the quote over whichever side of the app the job lands on. */
  onOpenQuote: (doc: LightsOsDocument) => void
}) {
  const [where, setWhere] = useState<Where>('today')
  /*
   * ONE HOUSE AT A TIME. A row opens the job's own page rather than dropping
   * straight into the editor: what somebody opens a job to ask in December is
   * where it stands, not what the lights look like.
   */
  const [openJob, setOpenJob] = useState<ProjectSummary | null>(null)
  const [setup, setSetup] = useState<SetupTab>('quotes')
  /** Which step of a job to open on. The board is the only thing that asks. */
  const [jobStep, setJobStep] = useState<'overview' | 'estimate' | 'scope'>('overview')

  /** Customers is a list or a board of the same records. See ui/Pipeline.tsx. */
  const [view, setView] = useState<'list' | 'pipe'>('list')
  /* And Jobs is a list or the work board. See ui/JobsBoard.tsx, JOB-01. */
  const [jobView, setJobView] = useState<'list' | 'board'>('list')
  // True while the customer list is showing one record rather than the list.
  const [record, setRecord] = useState(false)
  /*
    Stable, because Customers has it in an effect's dependency list: a new
    function every render would re-run that effect on every render.
  */
  const onRecord = useCallback((open: boolean) => setRecord(open), [])
  /*
   * ONE NAVIGATION, NOT TWO WITH ONE HIDDEN. Rendering both and hiding one in
   * CSS leaves a keyboard and a screen reader walking four destinations that
   * are not on the screen, under a second landmark with the same name as the
   * real one.
   */
  const phone = useIsPhone()
  /* The rest of the destinations, on a phone. See PHONE_IDS. */
  const [more, setMore] = useState(false)
  const closeMore = useCallback(() => setMore(false), [])
  const [items, setItems] = useState<ProjectSummary[] | null>(null)
  const company = useEditor((s) => s.company)
  const email = useAuth((s) => s.session?.user.email ?? '')

  /*
   * One read, shared. Jobs and Customers are the same rows asked two
   * different questions, and two components each fetching them is how the
   * customer list ends up a status behind the job list.
   */
  const refresh = () => listProjects().then(setItems).catch(() => setItems([]))
  useEffect(() => {
    refresh()
  }, [])

  const initials = useMemo(() => {
    const from = company.name.trim() || email
    const parts = from.split(/[\s@.]+/).filter(Boolean)
    return (parts[0]?.[0] ?? '?').toUpperCase() + (parts[1]?.[0] ?? '').toUpperCase()
  }, [company.name, email])

  /*
   * THE MARK GOES HOME FROM ANYWHERE, including out of a job you have opened.
   * One handler, used by the sidebar and by the phone header, so the two can
   * never come to mean different things.
   */
  const goHome = () => {
    setOpenJob(null)
    setWhere('today')
    setMore(false)
  }

  /* One way in to a destination, so the bar and the sheet cannot drift. */
  const go = (id: Where) => {
    setOpenJob(null)
    setWhere(id)
    setMore(false)
  }

  const nav = (
    <>
      {(phone ? [{ label: '', ids: PHONE_IDS }] : GROUPS).map((g) => (
        <div key={g.label || 'all'} className="ws-nav-run">
          {g.label && <p className="ws-nav-group">{g.label}</p>}
          {g.ids.map((gid) => {
            const place = PLACES.find((p) => p.id === gid)
            if (!place) return null
            const { id, label, short, Icon } = place
            return (
              <button
                key={id}
                className="ws-link"
                aria-current={where === id ? 'page' : undefined}
                onClick={() => go(id)}
              >
                <Icon size={18} />
                {phone ? short : label}
              </button>
            )
          })}
        </div>
      ))}
      {phone && (
        <button className="ws-link" aria-expanded={more} onClick={() => setMore(true)}>
          <IconMoreDots size={18} />
          More
        </button>
      )}
    </>
  )

  return (
    <div className="ws ws-frame">
      {/*
        Across the whole width, above the nav as well as the page. A strip that
        starts where the sidebar ends reads as belonging to one screen rather
        than to the account.
      */}
      <TrialNotice />
      <div className="ws-shell">
      {!phone && (
        <nav className="ws-nav" aria-label="Workspace">
        {/*
          The same door as in the editor: the mark takes you to the list.

          THE LIGHT MARK, BECAUSE THE RAIL IS DARK NOW. lightsos-dark.webp is
          the near black mark drawn for a light background; on the rail it is
          invisible, and an invisible logo reads as a broken image rather than
          as no logo. The phone bar below is still light and keeps the dark one.
        */}
        <button
          className="ws-brand"
          aria-label={`${BRAND}, your jobs`}
          onClick={goHome}
          title={`${BRAND} - your jobs`}
        >
          <img src="lightsos.webp" alt={BRAND} />
        </button>
        {/*
          THE ONE THING YOU START FROM SITS AT THE TOP OF THE SIDEBAR, above
          the destinations, exactly where the design puts Create new. It was in
          the header bar as a bright green pill on every screen, which made the
          loudest thing in the product a button most of a working day does not
          need, and put it a thumb's width from the account menu.
        */}
        <button className="ws-new" onClick={onStartNew}>
          <span aria-hidden="true">+</span> New design
        </button>
        <div className="ws-links">{nav}</div>
          <div className="ws-nav-foot">
            {/*
              IT IS CALLED DESIGNER, not Open designer.

              The package's own wording collided with the job page's Open
              design and with every row's Open <customer>: three controls whose
              names begin with the same word, going to three different places,
              two of them differing by two letters. Somebody listening to this
              screen rather than looking at it cannot tell them apart.

              The four destinations above it are bare nouns, and this is a
              fifth destination, so it reads as one.
            */}
            <button className="ws-link" onClick={onStartNew}>
              <IconDesign size={18} />
              Designer
            </button>
          </div>
        </nav>
      )}

      <div className="ws-main">
        {/*
          THE HEADER SAYS WHERE YOU ARE AND HOW IT IS HELD.
          Prototype shape: the trail on the left with the save state beneath
          it, and the one thing you start from on the right. The mark is on the
          phone only, because on a desktop it is already in the sidebar.
        */}
        <header className="ws-bar">
          <button
            className="ws-brand"
            aria-label={`${BRAND}, your jobs`}
            onClick={goHome}
            title={`${BRAND} - your jobs`}
          >
            <img src="lightsos-dark.webp" alt={BRAND} />
          </button>
          <p className="ws-crumbs">
            <b>
              {PLACES.find((x) => x.id === where)?.label ?? BRAND}
              {openJob ? ` / ${openJob.customer.trim() || openJob.name}` : ''}
            </b>
            <small>{company.name || 'Your business'}</small>
          </p>
          <div className="ws-bar-acts">
            {/*
              On a phone there is no sidebar to hold it, so the header keeps
              the one action. On a desktop it lives above the destinations.
            */}
            {phone && (
              <button className="btn primary" onClick={onStartNew}>
                <span aria-hidden="true">+</span> New design
              </button>
            )}
            <span className="ws-who" title={email}>
              {initials}
            </span>
          </div>
        </header>

        <div className="ws-scroll">
          <div className="ws-page">
            {where === 'jobs' &&
              (openJob ? (
                /*
                  ONE DOOR INTO A JOB. This used to be the record, and
                  Design & Quote was a second destination with its own copy of
                  the same list of houses and its own door into the same job.
                  The record is the first tab of this now.

                  Loading the document is what opens the editor: App renders
                  the workspace only while there is no photograph. So there is
                  nothing to do for onOpenDesign beyond the load the record
                  already did, and a second navigation would race it.
                */
                <DesignQuote
                  key={`${openJob.id}-${jobStep}`}
                  job={openJob}
                  startOn={jobStep}
                  onBack={() => setOpenJob(null)}
                  onChanged={refresh}
                  onOpenDesign={() => undefined}
                  onOpenQuote={onOpenQuote}
                />

              ) : jobView === 'board' ? (
                <>
                  <div className="ws-head">
                    <div>
                      <h1>The work board</h1>
                      <p>
                        Every column is worked out from what actually happened. Nothing here
                        drags, because there is no column to set.
                      </p>
                    </div>
                    <div className="ws-head-acts">
                      <div className="viewseg">
                        <button
                          aria-pressed={false}
                          onClick={() => setJobView('list')}
                        >
                          <IconRows size={16} />
                          List
                        </button>
                        <button aria-pressed onClick={() => setJobView('board')}>
                          <IconBoard size={16} />
                          Board
                        </button>
                      </div>
                    </div>
                  </div>
                  <JobsBoard
                    items={items ?? []}
                    onOpenJob={setOpenJob}
                    onGo={(dest, job) => {
                      if (dest === 'schedule') go('schedule')
                      else if (dest === 'today') go('today')
                      else {
                        setJobStep(dest === 'price' ? 'estimate' : dest === 'scope' ? 'scope' : 'overview')
                        setOpenJob(job)
                      }

                    }}
                  />
                </>
              ) : (
                <>
                  <div className="ws-head">
                    <div>
                      <h1>Every house this season</h1>
                      <p>What each one is worth, and which ones are still waiting on an answer.</p>
                    </div>
                    <div className="ws-head-acts">
                      <div className="viewseg">
                        <button aria-pressed onClick={() => setJobView('list')}>
                          <IconRows size={16} />
                          List
                        </button>
                        <button
                          aria-pressed={false}
                          onClick={() => setJobView('board')}
                        >
                          <IconBoard size={16} />
                          Board
                        </button>
                      </div>
                    </div>
                  </div>
                  <Projects
                    embedded
                    title=""
                    lede=""
                    items={items}
                    onRefresh={refresh}
                    onClose={() => undefined}
                    onStartNew={onStartNew}
                    onOpenJob={setOpenJob}
                  />
                </>
              ))}
            {where === 'today' &&
              (openJob ? (
                <JobOverview
                  job={openJob}
                  onBack={() => setOpenJob(null)}
                  onChanged={refresh}
                  onOpenDesign={() => undefined}
                  onOpenQuote={onOpenQuote}
                />
              ) : (
                <Today
                  items={items}
                  onChanged={refresh}
                  onOpenJob={setOpenJob}
                  onSeeAll={() => setWhere('jobs')}
                  onSeeMoney={() => setWhere('invoices')}
                  onSeeEstimates={() => setWhere('estimates')}
                  onSeeDesign={() => setWhere('jobs')}
                  onSeeWeek={() => setWhere('schedule')}
                />
              ))}
            {/*
              THE WEEK. A job opened from the queue goes to its own page, the
              same as a row anywhere else in the product, rather than to a
              second kind of job view that only the calendar knows about.
            */}
            {where === 'schedule' &&
              (openJob ? (
                <JobOverview
                  job={openJob}
                  onBack={() => setOpenJob(null)}
                  onChanged={refresh}
                  onOpenDesign={() => undefined}
                  onOpenQuote={onOpenQuote}
                />
              ) : (
                <Schedule items={items} onChanged={refresh} onOpenJob={setOpenJob} />
              ))}
            {/*
              ESTIMATES. Same records as Jobs, asked the opposite question, so
              a row opens the same job page a row anywhere else opens.
            */}
            {where === 'estimates' &&
              (openJob ? (
                <JobOverview
                  job={openJob}
                  onBack={() => setOpenJob(null)}
                  onChanged={refresh}
                  onOpenDesign={() => undefined}
                  onOpenQuote={onOpenQuote}
                />
              ) : (
                <Estimates items={items} onChanged={refresh} onOpenJob={setOpenJob} />
              ))}
            {/*
              SETTING UP THE SHOP, IN ONE PLACE, WITH TABS.

              Quote defaults, the letterhead, the roster and the pass were four
              blocks across three destinations, and no rule a person could
              guess said which held which. They are touched twice a year, so
              they are one screen and the tabs carry the difference.
            */}
            {where === 'settings' && (
              <>
                <div className="ws-head">
                  <div>
                    <h1>Settings</h1>
                    <p>How this shop works. Set once, and every job after it starts this way.</p>
                  </div>
                </div>
                <div className="ws-tools">
                  <div className="ws-tabs" role="tablist" aria-label="Settings">
                    {SETUP_TABS.map((t) => (
                      <button
                        key={t.id}
                        role="tab"
                        aria-pressed={setup === t.id}
                        aria-selected={setup === t.id}
                        onClick={() => setSetup(t.id)}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                {/* HOW A NEW QUOTE OPENS. SET-02. */}
                {setup === 'quotes' && <Settings chromeless />}
                {/* THE LETTERHEAD A CUSTOMER READS. */}
                {setup === 'letterhead' && <Business />}
                {/* WHO WORKS HERE, AND WHO IS AWAY. CREW-01. */}
                {setup === 'team' && <Team items={items} chromeless />}
                {/* THE PASS. */}
                {setup === 'plan' && <Billing />}
              </>
            )}
            {/* WHAT IS ON THE SHELF, AND HOW IT GOT THERE. INV-01. */}
            {where === 'stock' && <Stock />}
            {/* WHAT THE SEASON COST, AND WHAT IS STILL UNKNOWN. EXP-01, EXP-02. */}
            {where === 'costs' && <Costs items={items} />}
            {/*
              INVOICES. What is owed, and what is late.
            */}
            {where === 'invoices' &&
              (openJob ? (
                <JobOverview
                  job={openJob}
                  onBack={() => setOpenJob(null)}
                  onChanged={refresh}
                  onOpenDesign={() => undefined}
                  onOpenQuote={onOpenQuote}
                />
              ) : (
                <Invoices items={items} onOpenJob={setOpenJob} />
              ))}
            {/*
              A CARD OPENS THE JOB, wherever the card was. Setting openJob
              without rendering the page for it was a press that did nothing
              on this side of the app while working on the other two.
            */}
            {where === 'customers' && openJob && (
              <JobOverview
                job={openJob}
                onBack={() => setOpenJob(null)}
                onChanged={refresh}
                onOpenDesign={() => undefined}
                onOpenQuote={onOpenQuote}
              />
            )}
            {where === 'customers' && !openJob && (
              <>
                {!record && (
                <div className="ws-head">
                  <div>
                    <h1>{view === 'list' ? 'Who is waiting on you' : 'The pipeline'}</h1>
                    <p>
                      {view === 'list'
                        ? 'Grouped off your jobs. A job becomes a customer the moment it has a name on it.'
                        : 'Every column is worked out from what actually happened on the job. Nothing here is a field.'}
                    </p>
                  </div>
                  {/*
                    Two questions about the same records: who owes me an answer,
                    and where is everything. One list trying to be both is a
                    list that gets skimmed.
                  */}
                  <div className="ws-head-acts">
                    {/*
                      TWO WAYS TO LOOK AT ONE SET OF RECORDS, drawn the way the
                      design file draws them: a bordered pair with a mark on
                      each, not two words floating at the top of the page.
                    */}
                    <div className="viewseg">
                      <button aria-pressed={view === 'list'} onClick={() => setView('list')}>
                        <IconRows size={16} />
                        List
                      </button>
                      <button aria-pressed={view === 'pipe'} onClick={() => setView('pipe')}>
                        <IconBoard size={16} />
                        Board
                      </button>
                    </div>
                  </div>
                </div>
                )}
                {view === 'list' ? (
                  <Customers
                    items={items ?? []}
                    onOpened={() => undefined}
                    onChanged={refresh}
                    onRecord={onRecord}
                  />
                ) : (
                  <Pipeline items={items ?? []} onOpenJob={setOpenJob} onChanged={refresh} />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      </div>

      {phone && (
        <nav className="ws-tabbar" aria-label="Workspace">
          {nav}
        </nav>
      )}

      {/*
        THE REST OF THE PRODUCT, ON A PHONE.

        The same dialog every other sheet in here uses rather than a new bottom
        sheet pattern invented for the nav: one dialog everywhere was a line
        item in its own right, and a navigation that looks like nothing else in
        the app is the exact thing that reads as assembled.

        It lists every destination, including the four already on the bar, so
        somebody who opens it looking for Jobs finds Jobs rather than learning
        that this sheet holds the leftovers.
      */}
      {phone && more && (
        <Sheet title="Go to" variant="moresheet" onClose={closeMore}>
          <div className="morelist">
            {PLACES.map(({ id, label, Icon }) => (
              <button
                key={id}
                className="moreitem"
                aria-current={where === id ? 'page' : undefined}
                onClick={() => go(id)}
              >
                <Icon size={20} />
                {label}
              </button>
            ))}
            {/*
              A DIFFERENT MARK, because it is a different kind of thing. It
              sat under Design & Quote wearing the same pencil, so the sheet
              offered two rows with near identical names and identical icons
              going to two different places. This one starts from a
              photograph, which is what the mark says.
            */}
            <button
              className="moreitem is-start"
              onClick={() => {
                closeMore()
                onStartNew()
              }}
            >
              <IconPhoto size={20} />
              Designer
            </button>
          </div>
        </Sheet>
      )}
    </div>
  )
}
